import { Injectable, inject } from '@angular/core';
import { DatePicker, DatePickerOptions } from '@capacitor-community/date-picker';
import { TranslocoService } from '@jsverse/transloco';
import { PlatformService } from './platform.service';
import { LoggingService } from './logging.service';

export interface NativeDatePickerConfig {
  value?: Date;
  min?: Date;
  max?: Date;
  doneText?: string;
  cancelText?: string;
  theme?: 'light' | 'dark';
}

export interface NativeDatePickerResult {
  selected: boolean;
  value?: Date;
}

@Injectable({ providedIn: 'root' })
export class NativeDatePickerService {
  private readonly platform = inject(PlatformService);
  private readonly translocoService = inject(TranslocoService);
  private readonly loggingService = inject(LoggingService);

  isNativeAvailable(): boolean {
    return this.platform.isNativeMobile();
  }

  async showDatePicker(config: NativeDatePickerConfig = {}): Promise<NativeDatePickerResult> {
    if (!this.isNativeAvailable()) {
      this.loggingService.logWarning('NativeDatePickerService: Native picker not available on web');
      return { selected: false };
    }

    try {
      const pickerOptions = this.buildPickerOptions(config);
      const result = await DatePicker.present(pickerOptions);

      if (result.value) {
        return {
          selected: true,
          value: new Date(result.value),
        };
      }

      return { selected: false };
    } catch (error) {
      this.loggingService.logException(error, 'NativeDatePickerService: Error showing date picker');
      return { selected: false };
    }
  }

  private buildPickerOptions(config: NativeDatePickerConfig): DatePickerOptions {
    const now = new Date();

    const value = this.isValidDate(config.value) ? config.value : now;
    const min = this.isValidDate(config.min) ? config.min : undefined;
    const max = this.isValidDate(config.max) ? config.max : undefined;

    const pickerOptions: DatePickerOptions = {
      mode: 'date',
      date: this.formatDateForPicker(value),
      theme: config.theme || 'light',
      doneText: config.doneText || this.translocoService.translate('commonButtonsDone'),
      cancelText: config.cancelText || this.translocoService.translate('commonButtonsCancel'),
    };

    if (min) {
      pickerOptions.min = this.formatDateForPicker(min);
    }

    if (max) {
      pickerOptions.max = this.formatDateForPicker(max);
    }

    if (this.platform.isIOS()) {
      pickerOptions.ios = {
        style: 'wheels',
        locale: 'en_US'
      };
    }

    return pickerOptions;
  }

  private formatDateForPicker(date: Date | undefined): string {
    if (!date) {
      return new Date().toISOString();
    }
    return date.toISOString();
  }

  private isValidDate(date: Date | undefined | null): boolean {
    return date instanceof Date && !isNaN(date.getTime());
  }
}
